import {
  DEFAULT_SCHEDULING_ALERT_NOTE,
  MAX_PROFILE_PHOTO_BYTES,
  calculateBalance,
  forecastContributionCents,
  profilePhotoMimeTypes,
} from '@clinic/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  Appointment,
  AppointmentDraft,
  AppointmentType,
  AvailabilityRule,
  ChatAttachment,
  ChatReference,
  ClinicData,
  DashboardOperationalData,
  EmailDeliveryEvent,
  OutstandingAppointmentsCursor,
  OutstandingAppointmentsPage,
  Patient,
  PatientAppointmentCursor,
  PatientAppointmentPage,
  PatientDetailsResult,
  PatientDraft,
  PatientListCursor,
  PatientListPage,
  PatientPaymentCursor,
  PatientPaymentHistoryPage,
  PatientSchedulingAlertUpdate,
  ScheduleBlockDraft,
  ScheduleRangeResult,
  TaskDraft,
  TaskActivity,
  TaskStatus,
  TaskUpdateDraft,
} from '../domain/types';
import {
  clinicDateKey,
  clinicDateTimeLocalToIso,
  formatTime,
  onlyDigits,
} from '../domain/formatters';
import { useAuth } from '../app/AuthProvider';
import { FirebaseClinicRepository } from '../firebase/clinicRepository';
import type { PatientSearchResult } from '../firebase/clinicRepository';
import { clinicHolidayForLocalDate } from '@clinic/shared';
import { hasScheduleConflict } from '../domain/schedule';
import {
  isOutstandingCompletedAppointment,
  mergeCanonicalScheduleRange,
} from '../domain/operationalData';
import { patientSchedulingAlertMode } from '../domain/patients';
import {
  mergeChatMessagesById,
  mergeConversationSummaries,
  updateConversationUnreadCounts,
} from '../domain/conversations';
import { createDemoData } from './demoData';

interface ClinicDataContextValue {
  data: ClinicData;
  loading: boolean;
  error: string;
  notice: string;
  clearNotice: () => void;
  reload: () => Promise<void>;
  searchPatients: (
    query: string,
    offset?: number,
    activeOnly?: boolean,
  ) => Promise<{ items: PatientSearchResult[]; nextOffset: number | null }>;
  loadPatientsPage: (cursor?: PatientListCursor | null) => Promise<PatientListPage>;
  loadPatientForScheduling: (patientId: string) => Promise<Patient>;
  loadScheduleRange: (rangeStartAt: string, rangeEndAt: string) => Promise<ScheduleRangeResult>;
  subscribeToScheduleRange: (
    rangeStartAt: string,
    rangeEndAt: string,
    onReady: () => void,
    onConnectionError: (message: string) => void,
  ) => () => void;
  loadOutstandingAppointments: (
    cursor?: OutstandingAppointmentsCursor | null,
  ) => Promise<OutstandingAppointmentsPage>;
  loadDashboardOperationalData: (
    scheduleStartAt: string,
    scheduleEndAt: string,
    outstandingReferenceAt: string,
  ) => Promise<DashboardOperationalData>;
  loadPatientDetails: (patientId: string) => Promise<PatientDetailsResult>;
  loadPatientAppointments: (
    patientId: string,
    cursor: PatientAppointmentCursor,
  ) => Promise<PatientAppointmentPage>;
  loadPatientPaymentHistory: (
    patientId: string,
    cursor?: PatientPaymentCursor | null,
  ) => Promise<PatientPaymentHistoryPage>;
  createPatient: (draft: PatientDraft) => Promise<string>;
  updatePatient: (patientId: string, draft: PatientDraft) => Promise<void>;
  updatePatientSchedulingAlert: (
    patientId: string,
    update: PatientSchedulingAlertUpdate,
  ) => Promise<void>;
  updatePatientStatus: (
    patientId: string,
    status: 'active' | 'inactive',
    inactiveReason?: 'medical_discharge' | 'abandonment' | null,
  ) => Promise<void>;
  updatePatientStatuses: (
    patientIds: string[],
    status: 'active' | 'inactive',
    inactiveReason?: 'medical_discharge' | 'abandonment' | null,
  ) => Promise<string[]>;
  saveAppointmentType: (
    appointmentTypeId: string | null,
    appointmentType: Omit<AppointmentType, 'id'>,
  ) => Promise<void>;
  retireAppointmentType: (appointmentTypeId: string) => Promise<void>;
  saveAvailabilityRule: (
    availabilityRuleId: string | null,
    availabilityRule: Omit<AvailabilityRule, 'id'>,
  ) => Promise<void>;
  retireAvailabilityRule: (availabilityRuleId: string) => Promise<void>;
  createAppointment: (draft: AppointmentDraft) => Promise<string>;
  createScheduleBlock: (draft: ScheduleBlockDraft, recurrenceDates?: string[]) => Promise<void>;
  updateScheduleBlock: (blockId: string, draft: ScheduleBlockDraft) => Promise<void>;
  closeScheduleBlock: (blockId: string, action: 'cancelled' | 'retired') => Promise<void>;
  transitionAppointment: (
    appointmentId: string,
    status: Appointment['lifecycleStatus'],
  ) => Promise<void>;
  rescheduleAppointment: (appointmentId: string, newStartAt: string) => Promise<void>;
  cancelAppointment: (appointmentId: string, refundAmountCents?: number) => Promise<void>;
  setExpectedPaymentDate: (appointmentId: string, expectedPaymentAt: string) => Promise<void>;
  recordPayment: (appointmentId: string, amountCents: number, method?: string) => Promise<void>;
  recordRefund: (appointmentId: string, amountCents: number) => Promise<void>;
  loadEmailDeliveryHistory: (appointmentId: string) => Promise<EmailDeliveryEvent[]>;
  sendConfirmationEmail: (appointmentId: string) => Promise<void>;
  createTask: (draft: TaskDraft) => Promise<void>;
  updateTask: (taskId: string, patch: TaskUpdateDraft) => Promise<void>;
  updateTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  loadTaskHistory: (taskId: string) => Promise<TaskActivity[]>;
  sendMessage: (
    conversationId: string,
    text: string,
    attachment?: File,
    reference?: ChatReference,
  ) => Promise<void>;
  subscribeToMessages: (
    conversationId: string,
    onConnectionError: (message: string) => void,
  ) => () => void;
  loadOlderMessages: (conversationId: string) => Promise<boolean>;
  markConversationRead: (conversationId: string) => Promise<void>;
  markNotificationRead: (notificationId?: string | undefined) => Promise<void>;
  updateProfilePhoto: (file: File) => Promise<void>;
  loadProfilePhoto: (photoPath: string) => Promise<string>;
}

const ClinicDataContext = createContext<ClinicDataContextValue | null>(null);

function waitBriefly() {
  return new Promise<void>((resolve) => window.setTimeout(resolve, 180));
}

function computePaymentStatus(appointment: Appointment): Appointment['paymentStatus'] {
  if (
    appointment.amountRefundedCents === appointment.amountPaidCents &&
    appointment.amountPaidCents > 0
  ) {
    return 'refunded';
  }
  if (appointment.amountRefundedCents > 0) return 'partially_refunded';
  if (appointment.balanceCents <= 0) return 'paid';
  if (appointment.amountPaidCents > 0) return 'partially_paid';
  return 'pending';
}

export function rebuildDemoProjections(source: ClinicData): ClinicData {
  const appointments = source.appointments.map((appointment) => {
    const ledger = [
      ...(appointment.amountPaidCents > 0
        ? [{ entryType: 'payment' as const, amountCents: appointment.amountPaidCents }]
        : []),
      ...(appointment.amountRefundedCents > 0
        ? [{ entryType: 'refund' as const, amountCents: appointment.amountRefundedCents }]
        : []),
    ];
    const summary = calculateBalance(appointment.priceSnapshotCents, ledger);
    return {
      ...appointment,
      amountPaidCents: summary.amountPaidCents,
      amountRefundedCents: summary.amountRefundedCents,
      balanceCents: summary.balanceCents,
      paymentStatus: summary.paymentState,
    };
  });
  const patients = source.patients.map((patient) => ({
    ...patient,
    outstandingBalanceCents: appointments
      .filter(
        (appointment) =>
          appointment.patientId === patient.id && appointment.lifecycleStatus === 'completed',
      )
      .reduce((total, appointment) => total + appointment.balanceCents, 0),
  }));
  const labels = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  const currentYear = Number(
    new Intl.DateTimeFormat('en', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
    }).format(new Date()),
  );
  const financeMonths = labels.map((month, monthIndex) => {
    const appointmentsInMonth = (dateForAppointment: (appointment: Appointment) => string) =>
      appointments.filter((appointment) => {
        const parts = new Intl.DateTimeFormat('en', {
          timeZone: 'America/Sao_Paulo',
          year: 'numeric',
          month: 'numeric',
        }).formatToParts(new Date(dateForAppointment(appointment)));
        return (
          Number(parts.find((part) => part.type === 'year')?.value) === currentYear &&
          Number(parts.find((part) => part.type === 'month')?.value) === monthIndex + 1
        );
      });
    const forecastAppointments = appointmentsInMonth(
      (appointment) => appointment.expectedPaymentAt || appointment.startAt,
    );
    const financialOrigins = appointmentsInMonth((appointment) => appointment.startAt).filter(
      (appointment) => appointment.lifecycleStatus !== 'rescheduled',
    );
    const grossReceivedCents = financialOrigins.reduce(
      (total, appointment) => total + appointment.amountPaidCents,
      0,
    );
    const refundCents = financialOrigins.reduce(
      (total, appointment) => total + appointment.amountRefundedCents,
      0,
    );
    return {
      month,
      forecastCents: forecastAppointments.reduce(
        (total, appointment) =>
          total +
          forecastContributionCents(appointment.lifecycleStatus, appointment.priceSnapshotCents),
        0,
      ),
      grossReceivedCents,
      refundCents,
      netReceivedCents: grossReceivedCents - refundCents,
    };
  });
  return { ...source, appointments, patients, financeMonths };
}

export function preserveResolvedAppointmentNames(
  currentAppointments: Appointment[],
  incomingAppointments: Appointment[],
): Appointment[] {
  const resolvedNameByPatientId = new Map(
    currentAppointments
      .filter((appointment) => appointment.patientName !== 'Paciente')
      .map((appointment) => [appointment.patientId, appointment.patientName]),
  );

  return incomingAppointments.map((appointment) => {
    if (appointment.patientName !== 'Paciente') return appointment;
    const resolvedName = resolvedNameByPatientId.get(appointment.patientId);
    return resolvedName ? { ...appointment, patientName: resolvedName } : appointment;
  });
}

export function ClinicDataProvider({ children }: { children: ReactNode }) {
  const { user, demoMode } = useAuth();
  const initialData = useMemo(() => createDemoData(), []);
  const [data, setData] = useState(initialData);
  const dataRef = useRef(data);
  const demoEmailDeliveryHistoryRef = useRef(new Map<string, EmailDeliveryEvent[]>());
  const conversationRealtimeErrorRef = useRef('');
  const operationalRealtimeErrorRef = useRef('');
  const lastRealtimeNoticeRef = useRef({ message: '', shownAt: 0 });
  const loadedRepositoryRef = useRef<FirebaseClinicRepository | null>(null);
  const patientSchedulingAlertVersionsRef = useRef(new Map<string, number>());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const repository = useMemo(
    () => (user && !demoMode ? new FirebaseClinicRepository(user) : null),
    [demoMode, user],
  );

  useEffect(() => () => repository?.dispose(), [repository]);

  const commit = useCallback(
    (next: ClinicData) => {
      const projected = demoMode ? rebuildDemoProjections(next) : next;
      dataRef.current = projected;
      setData(projected);
    },
    [demoMode],
  );

  const announceRealtimeUpdate = useCallback((message: string) => {
    const now = Date.now();
    const previous = lastRealtimeNoticeRef.current;
    if (previous.message === message && now - previous.shownAt < 2_000) return;
    lastRealtimeNoticeRef.current = { message, shownAt: now };
    setNotice(message);
  }, []);

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      if (demoMode) {
        await waitBriefly();
        commit(createDemoData());
      } else if (repository) {
        const loaded = await repository.load();
        const next =
          loadedRepositoryRef.current === repository
            ? {
                ...loaded,
                appointments: preserveResolvedAppointmentNames(
                  dataRef.current.appointments,
                  loaded.appointments,
                ),
              }
            : loaded;
        loadedRepositoryRef.current = repository;
        commit(next);
      }
    } catch {
      setError('Não foi possível carregar os dados agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  }, [commit, demoMode, repository, user]);

  useEffect(() => {
    if (!user) return undefined;
    const timer = window.setTimeout(() => void reload(), 0);
    return () => window.clearTimeout(timer);
  }, [reload, user]);

  useEffect(() => {
    if (!repository || !user || loading) return undefined;
    return repository.subscribeToOperationalData(
      (patch) => {
        operationalRealtimeErrorRef.current = '';
        commit({ ...dataRef.current, ...patch });
      },
      announceRealtimeUpdate,
      (message) => {
        if (operationalRealtimeErrorRef.current === message) return;
        operationalRealtimeErrorRef.current = message;
        setNotice(message);
      },
    );
  }, [announceRealtimeUpdate, commit, loading, repository, user]);

  useEffect(() => {
    if (!repository || !user || loading) return undefined;
    return repository.subscribeToConversationSummaries(
      (summaries) => {
        conversationRealtimeErrorRef.current = '';
        const current = dataRef.current;
        commit({
          ...current,
          conversations: mergeConversationSummaries(current.conversations, summaries),
        });
      },
      (unreadCounts) => {
        conversationRealtimeErrorRef.current = '';
        const current = dataRef.current;
        commit({
          ...current,
          conversations: updateConversationUnreadCounts(
            current.conversations,
            user.id,
            unreadCounts,
          ),
        });
      },
      (message) => {
        if (conversationRealtimeErrorRef.current === message) return;
        conversationRealtimeErrorRef.current = message;
        setNotice(message);
      },
    );
  }, [commit, loading, repository, user]);

  const subscribeToMessages = useCallback(
    (conversationId: string, onConnectionError: (message: string) => void) => {
      if (!repository) return () => undefined;
      return repository.subscribeToMessages(
        conversationId,
        (messages) => {
          const current = dataRef.current;
          commit({
            ...current,
            conversations: current.conversations.map((conversation) => {
              if (conversation.id !== conversationId) return conversation;
              return {
                ...conversation,
                messages: mergeChatMessagesById(conversation.messages, messages),
              };
            }),
          });
        },
        onConnectionError,
      );
    },
    [commit, repository],
  );

  const loadOlderMessages = useCallback(
    async (conversationId: string) => {
      if (!repository) return false;
      const page = await repository.loadOlderMessages(conversationId);
      const current = dataRef.current;
      commit({
        ...current,
        conversations: current.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          return {
            ...conversation,
            messages: mergeChatMessagesById(page.messages, conversation.messages),
          };
        }),
      });
      return page.hasMore;
    },
    [commit, repository],
  );

  const loadEmailDeliveryHistory = useCallback(
    async (appointmentId: string): Promise<EmailDeliveryEvent[]> => {
      if (repository) return repository.loadEmailDeliveryHistory(appointmentId);
      const stored = demoEmailDeliveryHistoryRef.current.get(appointmentId);
      if (stored) return [...stored];
      const appointment = dataRef.current.appointments.find((item) => item.id === appointmentId);
      if (!appointment?.emailSentAt) return [];
      const patient = dataRef.current.patients.find((item) => item.id === appointment.patientId);
      const initialEvent: EmailDeliveryEvent = {
        id: `initial_demo_${appointmentId}`,
        appointmentId,
        recipientEmail: patient?.email ?? '',
        status: 'sent',
        requestedAt: appointment.emailSentAt,
        sentAt: appointment.emailSentAt,
        templateVersion: 'appointment-confirmation-v1',
        attemptNumber: 1,
        resend: false,
      };
      demoEmailDeliveryHistoryRef.current.set(appointmentId, [initialEvent]);
      return [initialEvent];
    },
    [repository],
  );

  const loadPatientForScheduling = useCallback(
    async (patientId: string): Promise<Patient> => {
      const patient = repository
        ? await repository.loadPatientForScheduling(patientId)
        : dataRef.current.patients.find((item) => item.id === patientId && item.active);
      if (!patient) throw new Error('Paciente não encontrado ou inativo. Atualize a busca.');
      commit({
        ...dataRef.current,
        patients: [
          patient,
          ...dataRef.current.patients.filter((current) => current.id !== patient.id),
        ],
      });
      return patient;
    },
    [commit, repository],
  );

  const loadPatientsPage = useCallback(
    async (cursor: PatientListCursor | null = null): Promise<PatientListPage> => {
      if (repository) return repository.loadPatientsPage(cursor);
      const pageSize = 24;
      const ordered = [...dataRef.current.patients].sort(
        (left, right) =>
          left.fullName.localeCompare(right.fullName, 'pt-BR') || left.id.localeCompare(right.id),
      );
      const startIndex = cursor
        ? ordered.findIndex(
            (patient) =>
              patient.fullName.localeCompare(cursor.fullName, 'pt-BR') > 0 ||
              (patient.fullName === cursor.fullName && patient.id > cursor.id),
          )
        : 0;
      const items = startIndex < 0 ? [] : ordered.slice(startIndex, startIndex + pageSize);
      const last = items.at(-1);
      return {
        items,
        nextCursor:
          last && startIndex + items.length < ordered.length
            ? { fullName: last.fullName, id: last.id }
            : null,
      };
    },
    [repository],
  );

  const loadPatientDetails = useCallback(
    async (patientId: string): Promise<PatientDetailsResult> => {
      const schedulingAlertVersion = patientSchedulingAlertVersionsRef.current.get(patientId) ?? 0;
      let result: PatientDetailsResult;
      if (repository) result = await repository.loadPatientDetails(patientId);
      else {
        const patient = dataRef.current.patients.find((item) => item.id === patientId);
        if (!patient) throw new Error('Paciente não encontrado ou sem permissão de acesso.');
        const allAppointments = dataRef.current.appointments
          .filter((appointment) => appointment.patientId === patientId)
          .sort(
            (left, right) =>
              right.startAt.localeCompare(left.startAt) || right.id.localeCompare(left.id),
          );
        const appointments = allAppointments.slice(0, 25);
        const last = appointments.at(-1);
        result = {
          patient,
          appointments,
          nextCursor:
            appointments.length < allAppointments.length && last
              ? { startAt: last.startAt, id: last.id }
              : null,
        };
      }
      if (
        (patientSchedulingAlertVersionsRef.current.get(patientId) ?? 0) !== schedulingAlertVersion
      ) {
        const currentPatient = dataRef.current.patients.find((patient) => patient.id === patientId);
        if (currentPatient) {
          result = {
            ...result,
            patient: {
              ...result.patient,
              requiresAdvancePayment: currentPatient.requiresAdvancePayment,
              schedulingAlertMode: patientSchedulingAlertMode(currentPatient),
              schedulingAlertNote: currentPatient.schedulingAlertNote ?? '',
            },
          };
        }
      }
      commit({
        ...dataRef.current,
        patients: [
          result.patient,
          ...dataRef.current.patients.filter((patient) => patient.id !== patientId),
        ],
        appointments: [
          ...dataRef.current.appointments.filter(
            (appointment) => !result.appointments.some((item) => item.id === appointment.id),
          ),
          ...result.appointments,
        ],
      });
      return result;
    },
    [commit, repository],
  );

  const loadPatientAppointments = useCallback(
    async (
      patientId: string,
      cursor: PatientAppointmentCursor,
    ): Promise<PatientAppointmentPage> => {
      let page: PatientAppointmentPage;
      if (repository) page = await repository.loadPatientAppointments(patientId, cursor);
      else {
        const allAppointments = dataRef.current.appointments
          .filter((appointment) => appointment.patientId === patientId)
          .sort(
            (left, right) =>
              right.startAt.localeCompare(left.startAt) || right.id.localeCompare(left.id),
          );
        const startIndex = Math.max(
          0,
          allAppointments.findIndex((appointment) => appointment.id === cursor.id) + 1,
        );
        const appointments = allAppointments.slice(startIndex, startIndex + 25);
        const last = appointments.at(-1);
        page = {
          appointments,
          nextCursor:
            startIndex + appointments.length < allAppointments.length && last
              ? { startAt: last.startAt, id: last.id }
              : null,
        };
      }
      commit({
        ...dataRef.current,
        appointments: [
          ...dataRef.current.appointments.filter(
            (appointment) => !page.appointments.some((item) => item.id === appointment.id),
          ),
          ...page.appointments,
        ],
      });
      return page;
    },
    [commit, repository],
  );

  const loadPatientPaymentHistory = useCallback(
    async (
      patientId: string,
      cursor: PatientPaymentCursor | null = null,
    ): Promise<PatientPaymentHistoryPage> => {
      if (repository) return repository.loadPatientPaymentHistory(patientId, cursor);
      const allEntries = dataRef.current.appointments
        .filter((appointment) => appointment.patientId === patientId)
        .flatMap((appointment) =>
          appointment.events
            .filter((event) => event.type === 'payment' || event.type === 'refund')
            .map((event) => ({
              id: event.id,
              appointmentId: appointment.id,
              appointmentTypeName: appointment.appointmentTypeName,
              appointmentStartAt: appointment.startAt,
              appointmentBalanceCents: appointment.balanceCents,
              entryType: event.type as 'payment' | 'refund',
              amountCents:
                event.type === 'payment'
                  ? appointment.amountPaidCents
                  : appointment.amountRefundedCents,
              occurredAt: event.occurredAt,
              actorName: event.actorName,
            })),
        )
        .sort(
          (left, right) =>
            right.occurredAt.localeCompare(left.occurredAt) || right.id.localeCompare(left.id),
        );
      const startIndex = cursor
        ? Math.max(0, allEntries.findIndex((entry) => entry.id === cursor.id) + 1)
        : 0;
      const items = allEntries.slice(startIndex, startIndex + 50);
      const last = items.at(-1);
      return {
        items,
        nextCursor:
          startIndex + items.length < allEntries.length && last
            ? { occurredAt: last.occurredAt, id: last.id }
            : null,
      };
    },
    [repository],
  );

  const loadScheduleRange = useCallback(
    async (rangeStartAt: string, rangeEndAt: string): Promise<ScheduleRangeResult> => {
      const canonical = repository
        ? await repository.loadScheduleRange(rangeStartAt, rangeEndAt)
        : {
            appointments: dataRef.current.appointments.filter(
              (appointment) => appointment.startAt < rangeEndAt && appointment.endAt > rangeStartAt,
            ),
            scheduleBlocks: dataRef.current.scheduleBlocks.filter(
              (block) =>
                block.status === 'active' &&
                block.startAt < rangeEndAt &&
                block.endAt > rangeStartAt,
            ),
          };
      const merged = mergeCanonicalScheduleRange(
        dataRef.current.appointments,
        dataRef.current.scheduleBlocks,
        canonical.appointments,
        canonical.scheduleBlocks,
        rangeStartAt,
        rangeEndAt,
      );
      commit({ ...dataRef.current, ...merged });
      return canonical;
    },
    [commit, repository],
  );

  const subscribeToScheduleRange = useCallback(
    (
      rangeStartAt: string,
      rangeEndAt: string,
      onReady: () => void,
      onConnectionError: (message: string) => void,
    ) => {
      if (!repository) {
        let active = true;
        void loadScheduleRange(rangeStartAt, rangeEndAt)
          .then(() => {
            if (active) onReady();
          })
          .catch((caught) => {
            if (!active) return;
            onConnectionError(
              caught instanceof Error
                ? caught.message
                : 'Não foi possível atualizar o período visível da agenda.',
            );
          });
        return () => {
          active = false;
        };
      }

      return repository.subscribeToScheduleRange(
        rangeStartAt,
        rangeEndAt,
        (canonical) => {
          const merged = mergeCanonicalScheduleRange(
            dataRef.current.appointments,
            dataRef.current.scheduleBlocks,
            canonical.appointments,
            canonical.scheduleBlocks,
            rangeStartAt,
            rangeEndAt,
          );
          commit({ ...dataRef.current, ...merged });
          onReady();
        },
        announceRealtimeUpdate,
        onConnectionError,
      );
    },
    [announceRealtimeUpdate, commit, loadScheduleRange, repository],
  );

  const loadOutstandingAppointments = useCallback(
    async (
      cursor: OutstandingAppointmentsCursor | null = null,
    ): Promise<OutstandingAppointmentsPage> => {
      if (repository) return repository.loadOutstandingAppointments(cursor);
      const pageSize = 24;
      const ordered = dataRef.current.appointments
        .filter(
          (appointment) =>
            appointment.balanceCents > 0 &&
            new Date(appointment.endAt) < new Date() &&
            !['cancelled', 'rescheduled'].includes(appointment.lifecycleStatus),
        )
        .sort(
          (left, right) =>
            right.balanceCents - left.balanceCents ||
            right.endAt.localeCompare(left.endAt) ||
            right.id.localeCompare(left.id),
        );
      const startIndex = cursor
        ? Math.max(0, ordered.findIndex((appointment) => appointment.id === cursor.id) + 1)
        : 0;
      const items = ordered.slice(startIndex, startIndex + pageSize);
      const last = items.at(-1);
      return {
        items,
        nextCursor:
          startIndex + items.length < ordered.length && last
            ? { balanceCents: last.balanceCents, endAt: last.endAt, id: last.id }
            : null,
      };
    },
    [repository],
  );

  const loadDashboardOperationalData = useCallback(
    async (
      scheduleStartAt: string,
      scheduleEndAt: string,
      outstandingReferenceAt: string,
    ): Promise<DashboardOperationalData> => {
      if (repository) {
        return repository.loadDashboardOperationalData(
          scheduleStartAt,
          scheduleEndAt,
          outstandingReferenceAt,
        );
      }
      const advancePaymentPatients = dataRef.current.patients.filter(
        (patient) => patient.requiresAdvancePayment,
      );
      const outstandingAppointments = dataRef.current.appointments
        .filter((appointment) =>
          isOutstandingCompletedAppointment(appointment, outstandingReferenceAt),
        )
        .sort(
          (left, right) =>
            right.balanceCents - left.balanceCents || right.endAt.localeCompare(left.endAt),
        );
      return {
        appointments: dataRef.current.appointments.filter(
          (appointment) =>
            appointment.startAt >= scheduleStartAt && appointment.startAt < scheduleEndAt,
        ),
        advancePaymentPatients: advancePaymentPatients.slice(0, 20),
        advancePaymentPatientsHasMore: advancePaymentPatients.length > 20,
        outstandingAppointments: outstandingAppointments.slice(0, 20),
        outstandingAppointmentsHasMore: outstandingAppointments.length > 20,
      };
    },
    [repository],
  );

  const updateTask = useCallback(
    async (taskId: string, patch: TaskUpdateDraft) => {
      if (!Object.keys(patch).length) return;
      if (repository) await repository.updateTask(taskId, patch);
      const now = new Date().toISOString();
      commit({
        ...dataRef.current,
        tasks: dataRef.current.tasks.map((task) =>
          task.id === taskId
            ? {
                ...task,
                assigneeId: patch.assigneeId ?? task.assigneeId,
                assigneeName:
                  patch.assigneeId == null
                    ? task.assigneeName
                    : (dataRef.current.users.find((candidate) => candidate.id === patch.assigneeId)
                        ?.displayName ?? task.assigneeName),
                status: patch.status ?? task.status,
                priority: patch.priority ?? task.priority,
                dueAt: patch.dueAt === undefined ? task.dueAt : (patch.dueAt ?? ''),
                updatedAt: now,
                completedAt: patch.status === 'completed' ? now : task.completedAt,
                activity: [
                  ...task.activity,
                  {
                    id: crypto.randomUUID(),
                    occurredAt: now,
                    actorName: user?.displayName ?? '',
                    text:
                      patch.status === 'completed'
                        ? 'Tarefa concluída; a pessoa solicitante foi notificada.'
                        : 'Ajustes da tarefa registrados.',
                  },
                ],
              }
            : task,
        ),
      });
      setNotice(
        patch.status === 'completed' ? 'Tarefa concluída.' : 'Ajustes da tarefa atualizados.',
      );
    },
    [commit, repository, user?.displayName],
  );

  const value = useMemo<ClinicDataContextValue>(
    () => ({
      data,
      loading,
      error,
      notice,
      clearNotice: () => setNotice(''),
      reload,
      async searchPatients(queryText, offset = 0, activeOnly = false) {
        if (repository) return repository.searchPatients(queryText, offset, activeOnly);
        const normalized = queryText
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('pt-BR');
        const digits = onlyDigits(queryText);
        const matched = dataRef.current.patients.filter(
          (patient) =>
            (!activeOnly || patient.active) &&
            (patient.fullName
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLocaleLowerCase('pt-BR')
              .includes(normalized) ||
              (digits.length >= 2 &&
                (patient.cpfDigits.includes(digits) ||
                  patient.primaryPhoneDigits.includes(digits)))),
        );
        const page = matched.slice(offset, offset + 12);
        return {
          items: page.map((patient) => ({
            patientId: patient.id,
            fullName: patient.fullName,
            birthDate: patient.birthDate,
            cpf: patient.cpf,
            primaryPhone: patient.primaryPhone,
            email: patient.email,
            requiresAdvancePayment: patient.requiresAdvancePayment,
            outstandingBalanceCents: patient.outstandingBalanceCents,
            active: patient.active,
          })),
          nextOffset: offset + page.length < matched.length ? offset + page.length : null,
        };
      },
      loadPatientsPage,
      loadPatientForScheduling,
      loadScheduleRange,
      subscribeToScheduleRange,
      loadOutstandingAppointments,
      loadDashboardOperationalData,
      loadPatientDetails,
      loadPatientAppointments,
      loadPatientPaymentHistory,
      async createPatient(draft) {
        const cpfDigits = onlyDigits(draft.cpf);
        if (dataRef.current.patients.some((patient) => patient.cpfDigits === cpfDigits)) {
          throw new Error(
            'Já existe um paciente com este CPF. Revise o cadastro antes de continuar.',
          );
        }
        const response = repository ? await repository.createPatient(draft) : null;
        const patientId = response?.patientId ?? `patient-${crypto.randomUUID()}`;
        const patient: Patient = {
          ...draft,
          id: patientId,
          cpfDigits,
          primaryPhoneDigits: onlyDigits(draft.primaryPhone),
        };
        commit({
          ...dataRef.current,
          patients: [
            patient,
            ...dataRef.current.patients.filter((candidate) => candidate.id !== patientId),
          ],
        });
        setNotice('Paciente cadastrado com sucesso.');
        return patientId;
      },
      async updatePatient(patientId, draft) {
        const duplicate = dataRef.current.patients.some(
          (patient) => patient.id !== patientId && patient.cpfDigits === onlyDigits(draft.cpf),
        );
        if (duplicate) throw new Error('Este CPF já está associado a outro paciente.');
        if (repository) await repository.updatePatient(patientId, draft);
        commit({
          ...dataRef.current,
          patients: dataRef.current.patients.map((patient) =>
            patient.id === patientId
              ? {
                  ...draft,
                  id: patientId,
                  cpfDigits: onlyDigits(draft.cpf),
                  primaryPhoneDigits: onlyDigits(draft.primaryPhone),
                }
              : patient,
          ),
        });
        setNotice('Cadastro atualizado com segurança.');
      },
      async updatePatientSchedulingAlert(patientId, update) {
        if (repository) await repository.updatePatientSchedulingAlert(patientId, update);
        patientSchedulingAlertVersionsRef.current.set(
          patientId,
          (patientSchedulingAlertVersionsRef.current.get(patientId) ?? 0) + 1,
        );
        commit({
          ...dataRef.current,
          patients: dataRef.current.patients.map((patient) =>
            patient.id === patientId
              ? {
                  ...patient,
                  requiresAdvancePayment: update.mode === 'dashboard_and_scheduling',
                  schedulingAlertMode: update.mode,
                  schedulingAlertNote: update.mode === 'discarded' ? '' : update.note.trim(),
                }
              : patient,
          ),
        });
        setNotice(
          update.mode === 'discarded'
            ? 'Observação ao agendar descartada.'
            : update.mode === 'scheduling_only'
              ? 'Observação mantida apenas no agendamento.'
              : 'Observação ao agendar atualizada.',
        );
      },
      async updatePatientStatus(patientId, status, inactiveReason = null) {
        if (repository) {
          await repository.updatePatientStatus(patientId, status, inactiveReason);
        }
        commit({
          ...dataRef.current,
          patients: dataRef.current.patients.map((patient) =>
            patient.id === patientId
              ? {
                  ...patient,
                  active: status === 'active',
                  inactiveReason: status === 'inactive' ? inactiveReason : null,
                }
              : patient,
          ),
        });
        setNotice(status === 'active' ? 'Paciente reativado.' : 'Paciente inativado.');
      },
      async updatePatientStatuses(patientIds, status, inactiveReason = null) {
        const changedPatientIds = repository
          ? (await repository.updatePatientStatuses(patientIds, status, inactiveReason)).patientIds
          : patientIds.filter((patientId) => {
              const patient = dataRef.current.patients.find((item) => item.id === patientId);
              return patient?.active !== (status === 'active');
            });
        const changedSet = new Set(changedPatientIds);
        commit({
          ...dataRef.current,
          patients: dataRef.current.patients.map((patient) =>
            changedSet.has(patient.id)
              ? {
                  ...patient,
                  active: status === 'active',
                  inactiveReason: status === 'inactive' ? inactiveReason : null,
                }
              : patient,
          ),
        });
        const count = changedPatientIds.length;
        setNotice(
          status === 'active'
            ? `${count} paciente${count === 1 ? '' : 's'} reativado${count === 1 ? '' : 's'}.`
            : `${count} paciente${count === 1 ? '' : 's'} inativado${count === 1 ? '' : 's'}.`,
        );
        return changedPatientIds;
      },
      async saveAppointmentType(appointmentTypeId, appointmentType) {
        if (!appointmentType.name.trim()) throw new Error('Informe o nome do atendimento.');
        if (
          !Number.isInteger(appointmentType.durationMinutes) ||
          appointmentType.durationMinutes < 15 ||
          appointmentType.durationMinutes > 240
        ) {
          throw new Error('A duração deve ficar entre 15 e 240 minutos.');
        }
        if (
          !Number.isSafeInteger(appointmentType.defaultPriceCents) ||
          appointmentType.defaultPriceCents < 0
        ) {
          throw new Error('Informe um valor válido em reais.');
        }
        const duplicateColor = dataRef.current.appointmentTypes.some(
          (candidate) =>
            candidate.id !== appointmentTypeId &&
            candidate.active &&
            appointmentType.active &&
            candidate.calendarColor.toUpperCase() === appointmentType.calendarColor.toUpperCase(),
        );
        if (duplicateColor) {
          throw new Error('Escolha uma cor diferente dos outros atendimentos ativos.');
        }
        const response = repository
          ? appointmentTypeId
            ? await repository.updateAppointmentType(appointmentTypeId, appointmentType)
            : await repository.createAppointmentType(appointmentType)
          : null;
        const id =
          appointmentTypeId ?? response?.appointmentTypeId ?? `type-${crypto.randomUUID()}`;
        const current = dataRef.current;
        commit({
          ...current,
          appointmentTypes: appointmentTypeId
            ? current.appointmentTypes.map((candidate) =>
                candidate.id === appointmentTypeId
                  ? { ...appointmentType, id: appointmentTypeId }
                  : candidate,
              )
            : [
                ...current.appointmentTypes.filter((candidate) => candidate.id !== id),
                { ...appointmentType, id },
              ].sort((left, right) => left.name.localeCompare(right.name, 'pt-BR')),
        });
        setNotice(
          appointmentTypeId ? 'Tipo de atendimento atualizado.' : 'Tipo de atendimento criado.',
        );
      },
      async retireAppointmentType(appointmentTypeId) {
        if (repository) await repository.retireAppointmentType(appointmentTypeId);
        const current = dataRef.current;
        commit({
          ...current,
          appointmentTypes: current.appointmentTypes.map((candidate) =>
            candidate.id === appointmentTypeId ? { ...candidate, active: false } : candidate,
          ),
        });
        setNotice('Tipo de atendimento inativado; o histórico foi preservado.');
      },
      async saveAvailabilityRule(availabilityRuleId, availabilityRule) {
        if (
          !Number.isInteger(availabilityRule.weekday) ||
          availabilityRule.weekday < 0 ||
          availabilityRule.weekday > 6 ||
          availabilityRule.startMinute < 0 ||
          availabilityRule.endMinute > 24 * 60 ||
          availabilityRule.startMinute >= availabilityRule.endMinute
        ) {
          throw new Error('Revise o dia e o intervalo de disponibilidade.');
        }
        if (
          availabilityRule.validUntilLocalDate &&
          availabilityRule.validUntilLocalDate < availabilityRule.validFromLocalDate
        ) {
          throw new Error('A data final deve ser igual ou posterior à data inicial.');
        }
        const response = repository
          ? availabilityRuleId
            ? await repository.updateAvailabilityRule(availabilityRuleId, availabilityRule)
            : await repository.createAvailabilityRule(availabilityRule)
          : null;
        const id =
          availabilityRuleId ??
          response?.availabilityRuleId ??
          `availability-${crypto.randomUUID()}`;
        const current = dataRef.current;
        commit({
          ...current,
          availabilityRules: availabilityRuleId
            ? current.availabilityRules.map((candidate) =>
                candidate.id === availabilityRuleId
                  ? { ...availabilityRule, id: availabilityRuleId }
                  : candidate,
              )
            : [
                ...current.availabilityRules.filter((candidate) => candidate.id !== id),
                { ...availabilityRule, id },
              ],
        });
        setNotice(
          availabilityRuleId ? 'Disponibilidade atualizada.' : 'Disponibilidade adicionada.',
        );
      },
      async retireAvailabilityRule(availabilityRuleId) {
        if (repository) await repository.retireAvailabilityRule(availabilityRuleId);
        const current = dataRef.current;
        commit({
          ...current,
          availabilityRules: current.availabilityRules.map((candidate) =>
            candidate.id === availabilityRuleId ? { ...candidate, active: false } : candidate,
          ),
        });
        setNotice('Disponibilidade encerrada.');
      },
      async createAppointment(draft) {
        const patient = dataRef.current.patients.find((item) => item.id === draft.patientId);
        const type = dataRef.current.appointmentTypes.find(
          (item) => item.id === draft.appointmentTypeId && item.active,
        );
        if (!patient || !type)
          throw new Error('Selecione um paciente e um tipo de atendimento válidos.');
        const start = new Date(draft.startAt);
        const end = new Date(start.getTime() + type.durationMinutes * 60_000);
        if (clinicHolidayForLocalDate(clinicDateKey(start))) {
          throw new Error('A agenda fica bloqueada automaticamente nos feriados.');
        }
        if (
          hasScheduleConflict(
            dataRef.current.appointments,
            dataRef.current.scheduleBlocks,
            start,
            end,
          )
        ) {
          throw new Error(
            'Este horário acabou de ficar indisponível. Atualize a agenda e escolha outro horário.',
          );
        }
        const response = repository ? await repository.createAppointment(draft) : null;
        const socialPrice = patient.socialCareEnabled
          ? patient.socialPricesCents[type.id]
          : undefined;
        const priceSnapshotCents =
          response?.priceSnapshotCents ?? socialPrice ?? type.defaultPriceCents;
        const appointmentId = response?.appointmentId ?? `appointment-${crypto.randomUUID()}`;
        const appointment: Appointment = {
          id: appointmentId,
          patientId: patient.id,
          patientName: patient.fullName,
          appointmentTypeId: type.id,
          appointmentTypeName: type.name,
          providerId:
            dataRef.current.users.find((candidate) => candidate.role === 'medico')?.id ??
            user?.id ??
            '',
          startAt: response?.startAt ?? start.toISOString(),
          endAt: response?.endAt ?? end.toISOString(),
          lifecycleStatus: 'pending_scheduling_confirmation',
          paymentStatus: 'pending',
          priceSnapshotCents,
          priceSource:
            response?.priceSource != null
              ? response.priceSource === 'social_override'
                ? 'social'
                : 'standard'
              : socialPrice !== undefined
                ? 'social'
                : 'standard',
          amountPaidCents: 0,
          amountRefundedCents: 0,
          balanceCents: priceSnapshotCents,
          expectedPaymentAt: draft.expectedPaymentAt,
          telehealthRoomUrl: 'https://meet.example.test/consulta-segura',
          emailSentAt: '',
          color: type.calendarColor,
          events: [
            {
              id: crypto.randomUUID(),
              type: 'pending_scheduling_confirmation',
              title: 'Aguardando confirmação de agendamento',
              occurredAt: new Date().toISOString(),
              actorName: user?.displayName ?? '',
              detail: `Valor definido em ${priceSnapshotCents} centavos.`,
            },
          ],
        };
        commit({
          ...dataRef.current,
          appointments: [
            ...dataRef.current.appointments.filter((candidate) => candidate.id !== appointmentId),
            appointment,
          ],
        });
        setNotice('Horário oferecido. Aguardando confirmação de agendamento.');
        return appointmentId;
      },
      async createScheduleBlock(draft, recurrenceDates = []) {
        const start = new Date(draft.startAt);
        const end = new Date(draft.endAt);
        if (end <= start) throw new Error('O horário final deve ser posterior ao horário inicial.');
        if (user?.role === 'secretaria' && !draft.reason.trim()) {
          throw new Error('Informe o motivo do bloqueio.');
        }
        const duration = end.getTime() - start.getTime();
        const occurrenceStarts = [clinicDateKey(start), ...recurrenceDates].map(
          (date) => new Date(clinicDateTimeLocalToIso(`${date}T${formatTime(start)}`)),
        );
        const conflict = occurrenceStarts.some((occurrenceStart) => {
          const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);
          return hasScheduleConflict(
            dataRef.current.appointments,
            dataRef.current.scheduleBlocks,
            occurrenceStart,
            occurrenceEnd,
          );
        });
        if (conflict) throw new Error('Existe uma consulta neste intervalo. Revise o bloqueio.');
        if (repository) {
          await repository.createScheduleBlock(draft, recurrenceDates);
          await reload();
          setNotice('Horário bloqueado na agenda.');
          return;
        }
        commit({
          ...dataRef.current,
          scheduleBlocks: [
            ...dataRef.current.scheduleBlocks,
            ...occurrenceStarts.map((occurrenceStart) => ({
              id: `block-${crypto.randomUUID()}`,
              ...draft,
              startAt: occurrenceStart.toISOString(),
              endAt: new Date(occurrenceStart.getTime() + duration).toISOString(),
              status: 'active' as const,
              createdBy: user?.displayName ?? '',
              updatedBy: user?.displayName ?? '',
            })),
          ],
        });
        setNotice('Horário bloqueado na agenda.');
      },
      async updateScheduleBlock(blockId, draft) {
        const start = new Date(draft.startAt);
        const end = new Date(draft.endAt);
        if (end <= start) throw new Error('O horário final deve ser posterior ao horário inicial.');
        if (user?.role === 'secretaria' && !draft.reason.trim()) {
          throw new Error('Informe o motivo do bloqueio.');
        }
        if (
          hasScheduleConflict(
            dataRef.current.appointments,
            dataRef.current.scheduleBlocks,
            start,
            end,
            undefined,
            blockId,
          )
        ) {
          throw new Error('Existe uma consulta ou bloqueio neste intervalo.');
        }
        if (repository) {
          await repository.updateScheduleBlock(blockId, draft);
          await reload();
          setNotice('Bloqueio atualizado com histórico preservado.');
          return;
        }
        commit({
          ...dataRef.current,
          scheduleBlocks: dataRef.current.scheduleBlocks.map((block) =>
            block.id === blockId
              ? { ...block, ...draft, updatedBy: user?.displayName ?? block.updatedBy }
              : block,
          ),
        });
        setNotice('Bloqueio atualizado com histórico preservado.');
      },
      async closeScheduleBlock(blockId, action) {
        if (repository) {
          await repository.closeScheduleBlock(blockId, action);
          await reload();
          setNotice('Bloqueio encerrado e horário liberado.');
          return;
        }
        commit({
          ...dataRef.current,
          scheduleBlocks: dataRef.current.scheduleBlocks.map((block) =>
            block.id === blockId
              ? { ...block, status: action, updatedBy: user?.displayName ?? block.updatedBy }
              : block,
          ),
        });
        setNotice('Bloqueio encerrado e horário liberado.');
      },
      async rescheduleAppointment(appointmentId, newStartAt) {
        const source = dataRef.current.appointments.find((item) => item.id === appointmentId);
        if (!source) throw new Error('Consulta não encontrada.');
        const start = new Date(newStartAt);
        const duration = new Date(source.endAt).getTime() - new Date(source.startAt).getTime();
        const end = new Date(start.getTime() + duration);
        if (clinicHolidayForLocalDate(clinicDateKey(start))) {
          throw new Error('A agenda fica bloqueada automaticamente nos feriados.');
        }
        const conflict = hasScheduleConflict(
          dataRef.current.appointments,
          dataRef.current.scheduleBlocks,
          start,
          end,
          appointmentId,
        );
        if (conflict) throw new Error('O novo horário está ocupado. Escolha outra opção.');
        const response = repository
          ? await repository.rescheduleAppointment(appointmentId, start.toISOString())
          : null;
        const now = new Date().toISOString();
        const destinationId = response?.appointmentId ?? `appointment-${crypto.randomUUID()}`;
        const sourceEvent = {
          id: crypto.randomUUID(),
          type: 'rescheduled' as const,
          title: 'Consulta reagendada',
          occurredAt: now,
          actorName: user?.displayName ?? '',
          detail: `De ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(source.startAt))} para ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(start)}.`,
        };
        const destination: Appointment = {
          ...source,
          id: destinationId,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          lifecycleStatus: 'pending_scheduling_confirmation',
          emailSentAt: '',
          events: [
            {
              ...sourceEvent,
              id: crypto.randomUUID(),
              type: 'pending_scheduling_confirmation',
              title: 'Novo horário aguardando confirmação de agendamento',
            },
          ],
        };
        commit({
          ...dataRef.current,
          appointments: [
            ...dataRef.current.appointments
              .filter((item) => item.id !== destinationId)
              .map((item) =>
                item.id === appointmentId
                  ? {
                      ...item,
                      lifecycleStatus: 'rescheduled' as const,
                      events: [...item.events, sourceEvent],
                    }
                  : item,
              ),
            destination,
          ],
        });
        setNotice('Consulta reagendada com o horário anterior preservado.');
      },
      async cancelAppointment(appointmentId, refundAmountCents) {
        const source = dataRef.current.appointments.find((item) => item.id === appointmentId);
        if (!source) throw new Error('Consulta não encontrada.');
        const refundable = source.amountPaidCents - source.amountRefundedCents;
        if (
          refundAmountCents !== undefined &&
          (refundAmountCents <= 0 || refundAmountCents > refundable)
        ) {
          throw new Error('A devolução não pode superar o valor recebido e ainda não devolvido.');
        }
        if (repository) {
          await repository.cancelAppointment(appointmentId, refundAmountCents);
          await reload();
          setNotice('Consulta cancelada com histórico e saldos atualizados.');
          return;
        }
        const refunded = source.amountRefundedCents + (refundAmountCents ?? 0);
        commit({
          ...dataRef.current,
          appointments: dataRef.current.appointments.map((item) =>
            item.id === appointmentId
              ? {
                  ...item,
                  lifecycleStatus: 'cancelled',
                  amountRefundedCents: refunded,
                  paymentStatus:
                    refunded > 0 && refunded === item.amountPaidCents
                      ? 'refunded'
                      : refunded > 0
                        ? 'partially_refunded'
                        : item.paymentStatus,
                  events: [
                    ...item.events,
                    {
                      id: crypto.randomUUID(),
                      type: 'cancelled',
                      title: 'Consulta cancelada',
                      occurredAt: new Date().toISOString(),
                      actorName: user?.displayName ?? '',
                      detail:
                        refundAmountCents !== undefined
                          ? 'Cancelamento e devolução registrados no histórico.'
                          : 'Cancelamento registrado sem devolução.',
                    },
                  ],
                }
              : item,
          ),
        });
        setNotice('Consulta cancelada com o histórico preservado.');
      },
      async setExpectedPaymentDate(appointmentId, expectedPaymentAt) {
        if (repository) await repository.setExpectedPaymentDate(appointmentId, expectedPaymentAt);
        commit({
          ...dataRef.current,
          appointments: dataRef.current.appointments.map((item) =>
            item.id === appointmentId ? { ...item, expectedPaymentAt } : item,
          ),
        });
        setNotice('Data esperada de pagamento atualizada.');
      },
      async transitionAppointment(appointmentId, status) {
        if (repository) await repository.transitionAppointment(appointmentId, status);
        const appointment = dataRef.current.appointments.find((item) => item.id === appointmentId);
        if (!appointment) return;
        const statusTitle: Record<Appointment['lifecycleStatus'], string> = {
          pending_scheduling_confirmation: 'Aguardando confirmação de agendamento',
          scheduled: 'Agendamento confirmado — aguardando confirmação da consulta',
          confirmed: 'Consulta confirmada',
          completed: 'Consulta realizada',
          no_show: 'Paciente faltou',
          rescheduled: 'Consulta reagendada',
          cancelled: 'Consulta cancelada',
        };
        const nextAppointments = dataRef.current.appointments.map((item) =>
          item.id === appointmentId
            ? {
                ...item,
                lifecycleStatus: status,
                events: [
                  ...item.events,
                  {
                    id: crypto.randomUUID(),
                    type: status,
                    title: statusTitle[status],
                    occurredAt: new Date().toISOString(),
                    actorName: user?.displayName ?? '',
                    detail: 'Alteração registrada no histórico imutável.',
                  },
                ],
              }
            : item,
        );
        const nextPatients =
          status === 'no_show'
            ? dataRef.current.patients.map((patient) =>
                patient.id === appointment.patientId
                  ? {
                      ...patient,
                      requiresAdvancePayment: true,
                      schedulingAlertMode: 'dashboard_and_scheduling' as const,
                      schedulingAlertNote:
                        patient.schedulingAlertNote?.trim() || DEFAULT_SCHEDULING_ALERT_NOTE,
                    }
                  : patient,
              )
            : dataRef.current.patients;
        commit({ ...dataRef.current, appointments: nextAppointments, patients: nextPatients });
        setNotice(`${statusTitle[status]} com histórico preservado.`);
      },
      async recordPayment(appointmentId, amountCents, method = 'pix') {
        if (!Number.isInteger(amountCents) || amountCents <= 0) {
          throw new Error('Informe um valor de pagamento maior que zero.');
        }
        const current = dataRef.current.appointments.find((item) => item.id === appointmentId);
        if (!current) return;
        if (amountCents > current.balanceCents) {
          throw new Error('O pagamento não pode ser maior que o saldo pendente.');
        }
        if (repository) {
          await repository.recordPayment(appointmentId, amountCents, method);
          await reload();
          setNotice('Pagamento registrado com sucesso.');
          return;
        }
        const next = {
          ...current,
          amountPaidCents: current.amountPaidCents + amountCents,
          balanceCents: current.balanceCents - amountCents,
        };
        next.paymentStatus = computePaymentStatus(next);
        next.events = [
          ...next.events,
          {
            id: crypto.randomUUID(),
            type: 'payment',
            title: 'Pagamento registrado',
            occurredAt: new Date().toISOString(),
            actorName: user?.displayName ?? '',
            detail: 'Lançamento financeiro preservado no histórico.',
          },
        ];
        commit({
          ...dataRef.current,
          appointments: dataRef.current.appointments.map((item) =>
            item.id === appointmentId ? next : item,
          ),
        });
        setNotice('Pagamento registrado com sucesso.');
      },
      async recordRefund(appointmentId, amountCents) {
        const current = dataRef.current.appointments.find((item) => item.id === appointmentId);
        if (!current) return;
        const refundable = current.amountPaidCents - current.amountRefundedCents;
        if (!Number.isInteger(amountCents) || amountCents <= 0 || amountCents > refundable) {
          throw new Error(
            'A devolução deve ser maior que zero e não pode superar o valor recebido.',
          );
        }
        if (repository) {
          await repository.recordRefund(appointmentId, amountCents);
          await reload();
          setNotice('Devolução registrada com segurança.');
          return;
        }
        const next = {
          ...current,
          amountRefundedCents: current.amountRefundedCents + amountCents,
        };
        next.paymentStatus = computePaymentStatus(next);
        next.events = [
          ...next.events,
          {
            id: crypto.randomUUID(),
            type: 'refund',
            title: 'Devolução registrada',
            occurredAt: new Date().toISOString(),
            actorName: user?.displayName ?? '',
            detail: 'Devolução preservada no livro financeiro.',
          },
        ];
        commit({
          ...dataRef.current,
          appointments: dataRef.current.appointments.map((item) =>
            item.id === appointmentId ? next : item,
          ),
        });
        setNotice('Devolução registrada com segurança.');
      },
      loadEmailDeliveryHistory,
      async sendConfirmationEmail(appointmentId) {
        if (repository) {
          const alreadySent = dataRef.current.appointments.find(
            (item) => item.id === appointmentId,
          )?.emailSentAt;
          const result = await repository.sendConfirmationEmail(
            appointmentId,
            Boolean(alreadySent),
          );
          await reload();
          setNotice(
            result.status === 'sent'
              ? 'E-mail enviado e registrado no histórico.'
              : 'Envio solicitado e registrado no histórico.',
          );
          return;
        }
        const history = await loadEmailDeliveryHistory(appointmentId);
        const sentAt = new Date().toISOString();
        const appointment = dataRef.current.appointments.find((item) => item.id === appointmentId);
        const patient = dataRef.current.patients.find((item) => item.id === appointment?.patientId);
        demoEmailDeliveryHistoryRef.current.set(appointmentId, [
          {
            id: `${history.length ? 'resend' : 'initial'}_demo_${crypto.randomUUID()}`,
            appointmentId,
            recipientEmail: patient?.email ?? '',
            status: 'sent',
            requestedAt: sentAt,
            sentAt,
            templateVersion: 'appointment-confirmation-v1',
            attemptNumber: history.length + 1,
            resend: history.length > 0,
          },
          ...history,
        ]);
        commit({
          ...dataRef.current,
          appointments: dataRef.current.appointments.map((appointment) =>
            appointment.id === appointmentId
              ? {
                  ...appointment,
                  emailSentAt: sentAt,
                  events: [
                    ...appointment.events,
                    {
                      id: crypto.randomUUID(),
                      type: 'email_sent',
                      title: 'E-mail de confirmação enviado',
                      occurredAt: sentAt,
                      actorName: user?.displayName ?? '',
                      detail: 'Entrega registrada pelo adaptador de e-mail.',
                    },
                  ],
                }
              : appointment,
          ),
        });
        setNotice('E-mail enviado e registrado no histórico.');
      },
      async createTask(draft) {
        const assignee = dataRef.current.users.find(
          (candidate) => candidate.id === draft.assigneeId,
        );
        if (!assignee) throw new Error('Selecione uma pessoa responsável.');
        const response = repository ? await repository.createTask(draft) : null;
        const now = new Date().toISOString();
        const taskId = response?.taskId ?? `task-${crypto.randomUUID()}`;
        commit({
          ...dataRef.current,
          tasks: [
            {
              id: taskId,
              ...draft,
              creatorId: user?.id ?? '',
              creatorName: user?.displayName ?? '',
              assigneeName: assignee.displayName,
              status: 'pending',
              createdAt: now,
              updatedAt: now,
              completedAt: '',
              activity: [
                {
                  id: crypto.randomUUID(),
                  occurredAt: now,
                  actorName: user?.displayName ?? '',
                  text: `Tarefa atribuída a ${assignee.displayName}.`,
                },
              ],
            },
            ...dataRef.current.tasks.filter((candidate) => candidate.id !== taskId),
          ],
        });
        setNotice('Tarefa criada e responsável notificado.');
      },
      updateTask,
      async updateTaskStatus(taskId, status) {
        await updateTask(taskId, { status });
      },
      async loadTaskHistory(taskId) {
        if (repository) return repository.loadTaskHistory(taskId);
        return dataRef.current.tasks.find((task) => task.id === taskId)?.activity ?? [];
      },
      async sendMessage(conversationId, text, attachment, reference) {
        const trimmed = text.trim();
        if (!trimmed && !attachment) return;
        const response = repository
          ? await repository.sendMessage(conversationId, trimmed, attachment, reference)
          : null;
        const displayAttachment: ChatAttachment | null = attachment
          ? {
              id: crypto.randomUUID(),
              name: attachment.name,
              mimeType: attachment.type,
              size: attachment.size,
              url: URL.createObjectURL(attachment),
            }
          : null;
        const sentAt = new Date().toISOString();
        const localMessage = {
          id: response?.messageId ?? `message-${crypto.randomUUID()}`,
          senderId: user?.id ?? '',
          senderName: user?.displayName ?? '',
          text: trimmed,
          sentAt,
          status: 'sent' as const,
          attachment: displayAttachment,
          reference: reference ?? null,
        };
        commit({
          ...dataRef.current,
          conversations: dataRef.current.conversations.map((conversation) =>
            conversation.id === conversationId
              ? {
                  ...conversation,
                  lastMessageAt: sentAt,
                  lastMessagePreview: trimmed || (attachment ? 'Anexo' : ''),
                  messages: mergeChatMessagesById([localMessage], conversation.messages),
                }
              : conversation,
          ),
        });
      },
      subscribeToMessages,
      loadOlderMessages,
      async markConversationRead(conversationId) {
        const conversation = dataRef.current.conversations.find(
          (item) => item.id === conversationId,
        );
        if (!conversation || !user) return;
        const lastMessage = conversation?.messages.at(-1);
        if (repository && lastMessage) {
          await repository.markConversationRead(conversationId, lastMessage.id);
        }
        commit({
          ...dataRef.current,
          conversations: dataRef.current.conversations.map((item) =>
            item.id === conversationId && user
              ? { ...item, unreadByUser: { ...item.unreadByUser, [user.id]: 0 } }
              : item,
          ),
        });
      },
      async markNotificationRead(notificationId) {
        const ids = dataRef.current.notifications
          .filter(
            (notification) =>
              (!notificationId || notification.id === notificationId) && !notification.read,
          )
          .map((notification) => notification.id);
        if (repository && ids.length) await repository.markNotificationsRead(ids);
        commit({
          ...dataRef.current,
          notifications: dataRef.current.notifications.map((notification) =>
            !notificationId || notification.id === notificationId
              ? { ...notification, read: true }
              : notification,
          ),
        });
      },
      async updateProfilePhoto(file) {
        if (!(profilePhotoMimeTypes as readonly string[]).includes(file.type)) {
          throw new Error('Escolha uma imagem JPG, PNG ou WebP.');
        }
        if (file.size <= 0 || file.size > MAX_PROFILE_PHOTO_BYTES) {
          throw new Error('A foto deve ter no máximo 2 MB.');
        }
        let photoPath: string | undefined;
        let photoUrl: string | undefined;
        if (repository) {
          ({ photoPath } = await repository.updateProfilePhoto(file));
        } else {
          photoUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              typeof reader.result === 'string'
                ? resolve(reader.result)
                : reject(new Error('Não foi possível ler a foto.'));
            reader.onerror = () => reject(new Error('Não foi possível ler a foto.'));
            reader.readAsDataURL(file);
          });
        }
        commit({
          ...dataRef.current,
          users: dataRef.current.users.map((candidate) =>
            candidate.id === user?.id
              ? {
                  ...candidate,
                  ...(photoPath ? { photoPath } : {}),
                  ...(photoUrl ? { photoUrl } : {}),
                }
              : candidate,
          ),
        });
        setNotice('Foto de perfil atualizada.');
      },
      async loadProfilePhoto(photoPath) {
        if (!repository) throw new Error('A foto de perfil não está disponível.');
        return repository.loadProfilePhoto(photoPath);
      },
    }),
    [
      commit,
      data,
      error,
      loadOlderMessages,
      loadEmailDeliveryHistory,
      loadPatientForScheduling,
      loadPatientsPage,
      loadScheduleRange,
      subscribeToScheduleRange,
      loadOutstandingAppointments,
      loadDashboardOperationalData,
      loadPatientAppointments,
      loadPatientDetails,
      loading,
      loadPatientPaymentHistory,
      notice,
      reload,
      repository,
      subscribeToMessages,
      updateTask,
      user,
    ],
  );

  return <ClinicDataContext.Provider value={value}>{children}</ClinicDataContext.Provider>;
}

export function useClinicData(): ClinicDataContextValue {
  const context = useContext(ClinicDataContext);
  if (!context) throw new Error('useClinicData must be used within ClinicDataProvider.');
  return context;
}
